import React, { useCallback } from 'react';
import { bool, func, shape, string } from 'prop-types';
import { Button, Checkbox, Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { Card } from '@cards/Card';
import DetailForm from '@components/DetailForm';
import DetailRow from '@components/DetailRow';
import LanguageFormItem from '@components/LanguageFormItem';
import { antFormType } from '../../../propTypes';

const DirectChildOrganisationUserEditWizard = ({ form, user, submitting, onSubmit }) => {
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    event => {
      event.preventDefault();
      form.validateFields((error, values) => {
        if (!error) onSubmit(values);
      });
    },
    [form, onSubmit],
  );

  return (
    <Card className="direct-child-organisation-user-form-card">
      <DetailForm formProps={{ hideRequiredMark: true, onSubmit: handleSubmit }}>
        <h3>{t('directChildOrganisationAdministration:users.edit.access')}</h3>
        <DetailRow
          label={t('directChildOrganisationAdministration:users.edit.organisation')}
          value={user.organisationName}
        />
        <DetailRow
          label={t('directChildOrganisationAdministration:users.edit.role')}
          value={t(`directChildOrganisationAdministration:users.roles.${user.role}`)}
        />
        <h3>{t('directChildOrganisationAdministration:users.edit.identity')}</h3>
        <DetailRow
          label={t('directChildOrganisationAdministration:users.edit.email')}
          value={user.email}
        />
        <Form.Item label={t('directChildOrganisationAdministration:users.edit.firstName')}>
          {form.getFieldDecorator('firstName', {
            initialValue: user.firstName,
            rules: [
              {
                required: true,
                message: t('directChildOrganisationAdministration:users.edit.errors.firstName'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <Form.Item label={t('directChildOrganisationAdministration:users.edit.lastName')}>
          {form.getFieldDecorator('lastName', {
            initialValue: user.lastName,
            rules: [
              {
                required: true,
                message: t('directChildOrganisationAdministration:users.edit.errors.lastName'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <Form.Item label={t('directChildOrganisationAdministration:users.edit.phoneNumber')}>
          {form.getFieldDecorator('phoneNumber', {
            initialValue: user.phoneNumber || '',
            rules: [
              {
                pattern: /\+[\d\s]+/,
                message: t('directChildOrganisationAdministration:users.edit.errors.phoneNumber'),
              },
            ],
          })(<Input disabled={submitting} />)}
        </Form.Item>
        <LanguageFormItem currentLanguage={user.language} form={form} />
        <Form.Item>
          {form.getFieldDecorator('careTeamMember', {
            initialValue: user.careTeamMember,
            valuePropName: 'checked',
          })(
            <Checkbox disabled={submitting}>
              {t('directChildOrganisationAdministration:users.edit.careTeamMember')}
            </Checkbox>,
          )}
        </Form.Item>
        <Button disabled={submitting} htmlType="submit" loading={submitting} type="primary">
          {t('directChildOrganisationAdministration:users.edit.submit')}
        </Button>
      </DetailForm>
    </Card>
  );
};

DirectChildOrganisationUserEditWizard.propTypes = {
  form: antFormType.isRequired,
  user: shape({
    email: string.isRequired,
    firstName: string.isRequired,
    language: string.isRequired,
    lastName: string.isRequired,
    organisationName: string.isRequired,
    phoneNumber: string,
    role: string.isRequired,
  }).isRequired,
  submitting: bool.isRequired,
  onSubmit: func.isRequired,
};

export default Form.create()(DirectChildOrganisationUserEditWizard);
